/**
 * NowPlayingHeader — pill cluster above the playlist tray.
 *
 * Per Figma 1:178: a static `Show:` prefix label, followed by a horizontal
 * row of category pills (TRENDING N, ACES N, …) and an overflow `»`
 * chevron when the pill row exceeds the column width.
 *
 *   1. TRENDING N  — accent variant; click clears any active filter
 *                    (back to "show everything"). N = total clip count.
 *   2. ACES N      — neutral variant; click toggles the ACES category
 *                    filter. N = count of clips matching the category.
 *
 * Additional categories live in `categories.ts::CATEGORY_DEFS`; pills
 * are rendered for every category present in that list.
 *
 * Filter state lives in `BrandedPlaylistFilterState` (a `{category}`
 * discriminant) and is consumed by BrandedEmbeddablePlaylist's
 * `visibleClips` computation, which delegates predicate logic to
 * `categories.ts::categoryMatches`.
 */
import {
  ReactNode,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Box } from "rebass/styled-components";
import styled from "styled-components";

import { CATEGORY_DEFS, countByCategory } from "../../categories";
import {
  BrandedLayoutVariantState,
  BrandedPlaylistClipsState,
  BrandedPlaylistFilterState,
} from "../../State";
import type { BrandedPlaylistCategory } from "../../State";
import { CrosshairIcon, FireIcon, ShieldIcon, TrendingIcon } from "./icons";

const SHOW_LABEL = "Show:";

/**
 * Header pills per Figma 1:178. Two visual variants both keyed off the
 * campaign accent — only alpha changes, never hue:
 *
 *   primary  → faint accent gradient fill, full accent border, WHITE
 *              label, accent-colored count text inline
 *   neutral  → 10% accent fill, 70% accent border, 60% accent text+icon
 *
 * `$active` (pressed-state) lifts the neutral variant to the primary
 * recipe so users see which pill drove the current filter. The primary
 * pill (TRENDING) doesn't need a pressed visual — clicking it clears the
 * filter, which is the implicit "everything" state when nothing else
 * is pressed.
 */
const PillBase = styled.button<{
  $accent?: boolean;
  $active?: boolean;
  $bottomPlaylist?: boolean;
}>`
  align-items: center;
  appearance: none;
  /* Pills share the playlist-row palette: --row-bg border, transparent
     idle fill, --row-bg active fill. Selected (filter applied) or the
     accent pill (Trending, which represents "everything") gets the
     filled treatment so the user can see which pill is driving the
     current view. */
  background: ${({ $accent, $active }) =>
    $accent || $active ? "var(--row-bg, #093358)" : "transparent"};
  border: 1px solid var(--row-border, var(--row-bg, #093358));
  border-radius: 999px;
  color: ${({ $accent, $active }) =>
    $accent || $active
      ? "#ffffff"
      : "var(--pill-text, color-mix(in srgb, var(--accent, #3C87D2) 60%, transparent))"};
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  /* Desktop: tight, low-profile pills so the rail above the row list
     reads as a compact filter strip rather than a chunky toolbar. The
     iframe only has ~226px of height to play with — every px of pill
     chrome eats into the rows below. Mobile keeps the larger touch-
     friendly sizing (overridden in the media query at the bottom). */
  font-size: 8px;
  font-weight: 800;
  gap: 3px;
  height: 18px;
  letter-spacing: 0.4px;
  padding: 0 6px;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;
  text-transform: uppercase;

  &:hover {
    background: ${({ $accent, $active }) =>
      $accent || $active
        ? "var(--row-bg, #093358)"
        : "color-mix(in srgb, var(--row-bg, #093358) 55%, transparent)"};
    color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid var(--accent, #3c87d2);
    outline-offset: 2px;
  }

  /* Bottom-playlist variant: bump pills up so they read at the airier
     scale we're using under the full-width player. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 10px;
    gap: 5px;
    height: 24px;
    letter-spacing: 0.5px;
    padding: 0 10px;
  `}

  /* Mobile keeps the original tap-target-friendly size — desktop's
     compact strip would feel cramped on a phone where the pill row
     is the primary slicer. */
  @media (max-width: 560px) {
    font-size: 9px;
    gap: 4px;
    height: 22px;
    letter-spacing: 0.5px;
    padding: 0 8px;
  }
`;

const PillIcon = styled(Box)`
  display: inline-flex;
  flex: 0 0 auto;
`;

/**
 * Static "Show:" prefix label. Sits flush-left of the pill scroller and
 * doesn't scroll with it — Figma anchors it as a column-level label, not
 * a pill.
 */
const ShowLabel = styled(Box)<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  /* Chalk (#8fa9af) is the design-system supporting-text color — same
     tint as MetaRow's meta text under the title — so the "Show:" prefix
     reads as a label, not chrome. Publisher review specifically called
     out the prior translucent white as off-spec; this lines up the
     prefix with the rest of the playlist's supporting type. */
  color: #8fa9af;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;

  /* Bottom-playlist variant: bump +2pt with the pill row. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 11px;
  `}

  @media (max-width: 560px) {
    font-size: 10px;
  }
`;

/**
 * Horizontal scroller for the pill row. Pills never wrap — overflow
 * scrolls sideways instead of stacking onto two lines (which would steal
 * vertical space from the rows below).
 */
const PillScroller = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  & > * {
    flex: 0 0 auto;
  }
  /* Scrollbar hidden — overflow signaled by the trailing chevron. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 560px) {
    gap: 6px;
  }
`;

/**
 * Overflow chevron `»` shown when the pill scroller has more content
 * past the visible viewport. Clicking advances the scroll position by
 * one pill-width-ish; users can also swipe/wheel-scroll directly.
 */
const OverflowChevron = styled.button<{ $visible: boolean }>`
  align-items: center;
  appearance: none;
  background: transparent;
  border: 1px solid var(--row-border, var(--row-bg, #093358));
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  height: 18px;
  justify-content: center;
  letter-spacing: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  padding: 0 5px;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 160ms ease;

  &:hover {
    background: color-mix(in srgb, var(--row-bg, #093358) 55%, transparent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent, #3c87d2);
    outline-offset: 2px;
  }

  @media (max-width: 560px) {
    font-size: 12px;
    height: 22px;
    padding: 0 8px;
  }
`;

const Wrap = styled(Box)`
  align-items: center;
  display: flex;
  gap: 3px;
  min-width: 0;
  width: 100%;

  @media (max-width: 560px) {
    gap: 8px;
  }
`;

/**
 * Inline count text inside the pill — NOT a separate background chip.
 * Heavy weight + flat accent color so the number reads at the brand's
 * full saturation alongside the label.
 */
const CountInline = styled(Box)`
  color: var(--pill-text, var(--accent, #3c87d2));
  font-weight: 800;
  margin-left: 3px;

  @media (max-width: 560px) {
    margin-left: 4px;
  }
`;

interface PillProps {
  icon: ReactNode;
  accent?: boolean;
  active?: boolean;
  count?: number;
  onClick?: () => void;
  ariaLabel?: string;
  bottomPlaylist?: boolean;
  children: ReactNode;
}

const Pill = ({
  icon,
  accent,
  active,
  count,
  onClick,
  ariaLabel,
  bottomPlaylist,
  children,
}: PillProps) => (
  <PillBase
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    aria-pressed={active}
    $accent={accent}
    $active={active}
    $bottomPlaylist={bottomPlaylist}
  >
    <PillIcon>{icon}</PillIcon>
    <Box as="span">
      {children}
      {typeof count === "number" && (
        <CountInline as="span">{count}</CountInline>
      )}
    </Box>
  </PillBase>
);

/**
 * Picks the icon for a given category. Centralized here (rather than in
 * `categories.ts`) so non-rendering code (predicates, counts) doesn't
 * have to pull in icon components.
 */
const iconForCategory = (id: BrandedPlaylistCategory): ReactNode => {
  switch (id) {
    case "aces":
      return <FireIcon />;
    case "clutches":
      return <ShieldIcon />;
    case "fourk":
    case "threek":
      // Crosshair reads heavier than the line-art Fire/Shield icons
      // at the same 14px target — drop a couple px so the visual
      // weight matches the other category pills.
      return <CrosshairIcon size={11} />;
    default:
      return <FireIcon />;
  }
};

export const NowPlayingHeader = () => {
  const clips = useRecoilValue(BrandedPlaylistClipsState);
  const [filter, setFilter] = useRecoilState(BrandedPlaylistFilterState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";

  // Per-category counts — surfaced inline on each pill so the user sees
  // the cost of a filter before clicking. Computed against the full clip
  // list (not the currently filtered tray) so numbers describe the
  // underlying playlist, not the user's recent slice of it.
  const counts = useMemo(() => {
    const total = clips?.length ?? 0;
    const perCategory: Record<BrandedPlaylistCategory, number> = {
      aces: 0,
      clutches: 0,
      fourk: 0,
      threek: 0,
    };
    if (clips) {
      CATEGORY_DEFS.forEach((def) => {
        perCategory[def.id] = countByCategory(clips, def.id);
      });
    }
    return { total, perCategory };
  }, [clips]);

  /**
   * Render up to two category pills, drawn from CATEGORY_DEFS in
   * priority order (aces → clutches → 4K → 3K). Pills with a zero
   * count are skipped — there's no value in showing "ACES 0" if the
   * playlist has none. When the prestigious categories are empty the
   * 4K / 3K fallbacks keep the pill row populated so the user always
   * has at least one or two ways to slice the playlist.
   */
  const visibleCategoryDefs = useMemo(
    () =>
      CATEGORY_DEFS.filter(
        (def) => (counts.perCategory[def.id] ?? 0) > 0,
      ).slice(0, 2),
    [counts.perCategory],
  );

  const handleTrendingClick = () => {
    // Trending == "show everything", so clicking it always clears the
    // filter. No toggle behavior — there's no "more than everything"
    // state to flip into.
    setFilter(null);
  };

  const handleCategoryClick = (id: BrandedPlaylistCategory) => {
    setFilter((prev) =>
      prev && prev.category === id ? null : { category: id },
    );
  };

  /**
   * Overflow detection for the trailing `»` chevron. Hidden when the
   * pill row fits in the visible viewport; shown when there's more to
   * the right. Identical pattern to PlaylistColumn's "scroll for more"
   * chevron — direct DOM measurement, no recoil atom.
   */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [chevronVisible, setChevronVisible] = useState(false);

  const measureOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const more = el.scrollWidth - el.clientWidth - el.scrollLeft > 4;
    setChevronVisible(more);
  }, []);

  useEffect(() => {
    measureOverflow();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", measureOverflow, { passive: true });
    const ro = new ResizeObserver(measureOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measureOverflow);
      ro.disconnect();
    };
  }, [measureOverflow, clips?.length]);

  const handleChevronClick = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: 120, behavior: "smooth" });
  };

  return (
    <Wrap>
      <ShowLabel $bottomPlaylist={isBottomPlaylist}>{SHOW_LABEL}</ShowLabel>
      <PillScroller ref={scrollerRef}>
        <Pill
          icon={<TrendingIcon />}
          accent
          active={!filter}
          count={counts.total}
          onClick={handleTrendingClick}
          ariaLabel="Show all clips"
          bottomPlaylist={isBottomPlaylist}
        >
          Trending
        </Pill>
        {visibleCategoryDefs.map((def) => {
          const active = !!filter && filter.category === def.id;
          return (
            <Pill
              key={def.id}
              icon={iconForCategory(def.id)}
              count={counts.perCategory[def.id] ?? 0}
              active={active}
              onClick={() => handleCategoryClick(def.id)}
              ariaLabel={
                active ? `Clear ${def.label} filter` : `Filter to ${def.label}`
              }
              bottomPlaylist={isBottomPlaylist}
            >
              {def.label}
            </Pill>
          );
        })}
      </PillScroller>
      <OverflowChevron
        type="button"
        aria-label="Scroll category pills"
        onClick={handleChevronClick}
        $visible={chevronVisible}
      >
        »
      </OverflowChevron>
    </Wrap>
  );
};
